import React, { useEffect, useState } from "react";
import { api, fmt } from "@/api";
import { JerseyBadge } from "@/components/Shared";

const key = (rid, w, j) => `${rid}:${w}:${j}`;

export default function ScoreEditor() {
  const [heatOptions, setHeatOptions] = useState([]);
  const [selectedHeatId, setSelectedHeatId] = useState("");
  const [data, setData] = useState(null);
  const [cells, setCells] = useState({});
  const [extra, setExtra] = useState({});
  const [note, setNote] = useState("");
  const [clockMin, setClockMin] = useState(15);

  const loadHeats = async () => {
    try {
      const sched = await api.schedule();
      const opts = [];
      (sched.divisions || []).forEach((d) =>
        (d.rounds || []).forEach((rnd) =>
          (rnd.heats || []).forEach((h) =>
            opts.push({ id: h.id, label: `${d.name} · ${rnd.name} · ${h.name} — ${h.state}` })
          )
        )
      );
      setHeatOptions(opts);
    } catch (e) {}
  };

  useEffect(() => { loadHeats(); }, []);

  const loadScores = async (id) => {
    if (!id) { setData(null); return; }
    const d = await api.heatScores(id);
    const c = {};
    d.riders.forEach((r) => {
      Object.entries(r.waves || {}).forEach(([w, judges]) => {
        Object.entries(judges).forEach(([j, v]) => { c[key(r.rider_id, Number(w), Number(j))] = String(v); });
      });
    });
    setCells(c);
    setExtra({});
    setData(d);
  };

  const onSelect = (id) => { setSelectedHeatId(id); loadScores(id); };

  const rowsFor = (r) => {
    let maxIdx = -1;
    Object.keys(r.waves || {}).forEach((w) => { if (Number(w) > maxIdx) maxIdx = Number(w); });
    return Math.min(10, Math.max(maxIdx + 1, 0) + (extra[r.rider_id] || 0));
  };
  const waveAvg = (rid, w) => {
    const vals = [1, 2, 3].map((j) => cells[key(rid, w, j)]).filter((v) => v !== "" && v != null && !isNaN(v)).map(Number);
    return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
  };
  const riderTotal = (r) => {
    const rows = rowsFor(r);
    // Sum of the rider's two highest-scoring waves (each wave = judges' average).
    const avgs = [];
    for (let w = 0; w < rows; w++) { const a = waveAvg(r.rider_id, w); if (a != null) avgs.push(a); }
    avgs.sort((a, b) => b - a);
    return (avgs[0] || 0) + (avgs[1] || 0);
  };

  const saveCell = async (rid, w, j) => {
    const raw = cells[key(rid, w, j)];
    const val = raw === "" || raw == null ? null : Number(raw);
    if (val != null && (isNaN(val) || val < 0 || val > 10)) { setNote("Score must be 0–10"); return; }
    try {
      await api.adminSetScore(selectedHeatId, { rider_id: rid, judge_num: j, wave_index: w, value: val });
      setNote("Saved ✓");
      setTimeout(() => setNote(""), 900);
    } catch (e) {
      setNote(e?.response?.data?.detail || "Save failed");
    }
  };

  const closeHeat = async () => {
    if (!window.confirm("Close this heat? Results lock in and the top 2 advance.")) return;
    await api.closeHeat(selectedHeatId);
    setNote("Heat closed ✓");
    setTimeout(() => setNote(""), 1500);
    loadHeats();
    loadScores(selectedHeatId);
  };
  const reopenHeat = async () => {
    await api.openHeat(selectedHeatId);
    setNote("Heat reopened ✓");
    setTimeout(() => setNote(""), 1500);
    loadHeats();
    loadScores(selectedHeatId);
  };
  const openHeat = async () => {
    await api.openHeat(selectedHeatId);
    setNote("Heat opened — now live ✓");
    setTimeout(() => setNote(""), 1500);
    loadHeats();
    loadScores(selectedHeatId);
  };
  const restartHeat = async () => {
    if (!window.confirm("Restart this heat? This ERASES all its scores and sets it back to pending.")) return;
    await api.resetHeat(selectedHeatId);
    setNote("Heat restarted (scores wiped) ✓");
    setTimeout(() => setNote(""), 1800);
    loadHeats();
    loadScores(selectedHeatId);
  };
  const clockSet = async () => {
    const mins = Number(clockMin);
    if (!mins || mins <= 0) { setNote("Enter minutes first"); return; }
    await api.startTimer(selectedHeatId, mins);
    setNote(`Clock set to ${mins}:00 ✓`);
    setTimeout(() => setNote(""), 1500);
  };
  const clockAdjust = async (seconds) => {
    await api.adjustTimer(selectedHeatId, seconds);
    setNote(seconds > 0 ? `+${seconds / 60} min added ✓` : `${seconds / 60} min removed ✓`);
    setTimeout(() => setNote(""), 1500);
  };
  const clockClear = async () => {
    await api.stopTimer(selectedHeatId);
    setNote("Clock cleared ✓");
    setTimeout(() => setNote(""), 1500);
  };

  const allScored = data && data.riders.length > 0 && data.riders.every((r) => {
    for (let w = 0; w < rowsFor(r); w++) if (waveAvg(r.rider_id, w) != null) return true;
    return false;
  });

  return (
    <div className="brutal p-4 mb-6" data-testid="score-editor-panel">
      <h2 className="font-display text-xl font-black uppercase tracking-tight mb-1">Score Editor</h2>
      <p className="font-display text-xs text-gray-500 mb-3">Adjust any rider's wave scores on any heat — past or present. Changes save instantly. Close the heat once every score is in.</p>

      <select
        data-testid="score-editor-heat-select"
        value={selectedHeatId}
        onChange={(e) => onSelect(e.target.value)}
        className="w-full brutal-2 p-2 font-display text-sm bg-white mb-3"
      >
        <option value="">Pick a heat to edit…</option>
        {heatOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
      </select>

      {data && (
        <>
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <div className="font-display font-black uppercase text-sm">
              {data.heat.division_name} · {data.heat.round_name} · {data.heat.name}
              <span className={`ml-2 text-[10px] px-2 py-0.5 ${data.heat.state === "closed" ? "bg-black text-white" : data.heat.state === "open" ? "bg-[#E63946] text-white" : "brutal-2 text-gray-500"}`}>{data.heat.state}</span>
            </div>
            {note && <span className="font-display text-xs font-black uppercase text-green-700" data-testid="score-editor-note">{note}</span>}
          </div>

          {/* Admin override: open / close / restart */}
          <div className="flex flex-wrap gap-2 mb-2" data-testid="score-editor-heat-controls">
            {data.heat.state !== "open" && (
              <button data-testid="score-editor-open" onClick={openHeat} className="brutal bg-green-600 text-white font-display font-black uppercase text-xs px-4 py-2 active:scale-95 transition-transform">▶ Open / Make Live</button>
            )}
            {data.heat.state !== "closed" ? (
              <button data-testid="score-editor-close" onClick={closeHeat} className={`brutal font-display font-black uppercase text-xs px-4 py-2 active:scale-95 transition-transform ${allScored ? "bg-black text-white" : "bg-white"}`}>■ Close Heat</button>
            ) : (
              <button data-testid="score-editor-reopen" onClick={reopenHeat} className="brutal-2 font-display font-black uppercase text-xs px-4 py-2">↻ Reopen</button>
            )}
            <button data-testid="score-editor-restart" onClick={restartHeat} className="brutal-2 bg-white font-display font-black uppercase text-xs px-4 py-2 text-[#E63946]">⟲ Restart (wipe scores)</button>
          </div>

          {/* Clock control — set / reverse (add) / subtract / clear */}
          <div className="brutal-2 bg-gray-50 p-2 mb-3 flex flex-wrap items-center gap-2" data-testid="score-editor-clock">
            <span className="font-display text-[10px] uppercase tracking-widest text-gray-500">Heat clock</span>
            <input
              data-testid="clock-minutes-input"
              type="number" min="1" max="120"
              value={clockMin}
              onChange={(e) => setClockMin(e.target.value)}
              className="brutal-2 w-16 p-1 text-center font-score text-base bg-white"
            />
            <button data-testid="clock-set" onClick={clockSet} className="brutal-2 bg-white font-display font-black uppercase text-[10px] px-3 py-1">Set clock</button>
            <button data-testid="clock-add" onClick={() => clockAdjust(60)} className="brutal-2 bg-white font-display font-black uppercase text-[10px] px-3 py-1">+1:00 (reverse)</button>
            <button data-testid="clock-sub" onClick={() => clockAdjust(-60)} className="brutal-2 bg-white font-display font-black uppercase text-[10px] px-3 py-1">−1:00</button>
            <button data-testid="clock-clear" onClick={clockClear} className="brutal-2 bg-white font-display font-black uppercase text-[10px] px-3 py-1">Clear</button>
          </div>

          {data.heat.state !== "closed" && (
            <p className={`font-display text-xs mb-3 ${allScored ? "text-green-700" : "text-gray-500"}`} data-testid="score-editor-ready">
              {allScored ? "✓ Every rider has a score — ready to close." : "Enter scores below; the Close button darkens once all riders are scored."}
            </p>
          )}

          <div className="space-y-3">
            {data.riders.map((r) => {
              const rows = rowsFor(r);
              return (
                <div key={r.rider_id} className="brutal-2 p-3" data-testid={`score-editor-rider-${r.rider_id}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <JerseyBadge jersey={r.jersey} size="sm" />
                    <span className="font-display font-black uppercase text-sm flex-1 min-w-0 truncate">{r.name}</span>
                    <span className="font-display text-[10px] uppercase tracking-widest text-gray-400">Total</span>
                    <span className="font-score text-2xl" data-testid={`score-editor-total-${r.rider_id}`}>{fmt(riderTotal(r))}</span>
                  </div>
                  <div className="grid grid-cols-[3rem_1fr_1fr_1fr_3.5rem] gap-1 items-center">
                    <div />
                    {[1, 2, 3].map((j) => <div key={j} className="font-display text-[10px] uppercase tracking-widest text-gray-500 text-center">J{j}</div>)}
                    <div className="font-display text-[10px] uppercase tracking-widest text-gray-500 text-center">Avg</div>
                    {Array.from({ length: rows }, (_, w) => w).map((w) => {
                      const avg = waveAvg(r.rider_id, w);
                      return (
                        <React.Fragment key={w}>
                          <div className="font-display text-[11px] font-black uppercase text-gray-500">W{w + 1}</div>
                          {[1, 2, 3].map((j) => (
                            <input
                              key={j}
                              data-testid={`score-cell-${r.rider_id}-${w}-${j}`}
                              type="number"
                              min="0"
                              max="10"
                              step="0.5"
                              value={cells[key(r.rider_id, w, j)] ?? ""}
                              onChange={(e) => setCells((c) => ({ ...c, [key(r.rider_id, w, j)]: e.target.value }))}
                              onBlur={() => saveCell(r.rider_id, w, j)}
                              className="brutal-2 py-1 text-center font-score text-base bg-white w-full focus:outline-none focus:bg-yellow-50"
                              placeholder="—"
                            />
                          ))}
                          <div className="font-score text-base text-center text-gray-600">{avg != null ? fmt(avg) : "—"}</div>
                        </React.Fragment>
                      );
                    })}
                  </div>
                  {rows < 10 && (
                    <button
                      data-testid={`score-editor-addwave-${r.rider_id}`}
                      onClick={() => setExtra((x) => ({ ...x, [r.rider_id]: (x[r.rider_id] || 0) + 1 }))}
                      className="mt-2 font-display font-black uppercase text-[11px] tracking-widest underline"
                    >
                      + Add wave
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
